import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getForms, deleteForm, createForm, updateForm, getFieldTypes } from '../api/formapi';
import FormModal from './FormModal';
import Layout from './container/Layout';
import FormAttributeTooltip from './FormAttributeTooltip';

const FormList = () => {
    // States for form list
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tooltipVisible, setTooltipVisible] = useState(null);
    const [copiedField, setCopiedField] = useState(null);

    // States for modal/popup
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentForm, setCurrentForm] = useState({
        formId: '',
        sourceUrl: '',
        formName: '',
        description: '',
        alias: '',       // Added new field
        formAttributeId: '', // Added new field
        fields: []
    });

    // State for form field management
    const [fieldState, setFieldState] = useState({
        fieldName: '',
        fieldType: 'text',
        required: false,
        defaultValue: '',
        validation: {
            minLength: '',
            maxLength: '',
            regex: ''
        }
    });

    const fieldTypes = getFieldTypes();

    // Column information tooltips
    const columnInfo = {
        alias: "A short, friendly name for the form used for easy identification.",
        formId: "The unique identifier used in the system to reference this form.",
        formAttributeId: "The attribute ID used for integration with external systems. Click to copy.",
        formName: "The HTML name attribute of the form (the name='value' attribute in the form tag).",
        sourceUrl: "The URL where this form appears or is hosted.",
        fields: "The number of input fields included in this form.",
        createdAt: "The date when this form was initially created."
    };

    // Detailed usage instructions for Form Attribute ID
    const formAttributeUsageInstructions = `
        <div class="usage-title">How to Use Form Attribute ID</div>
        <div class="usage-steps">
            <p><strong>Step 1:</strong> Copy the Form Attribute ID by clicking the clipboard icon.</p>
            <p><strong>Step 2:</strong> In your HTML form, add a new attribute to the <code>&lt;form&gt;</code> tag:</p>
            <div class="code-example">
                &lt;form <span class="highlight">data-form-submission-id="<span class="placeholder">PASTE_ID_HERE</span>"</span> ...other attributes...&gt;
            </div>
            <p><strong>Step 3:</strong> This connects your form to our tracking system and enables advanced analytics and form submission handling.</p>
            <div class="usage-note">
                <strong>Note:</strong> The attribute must be added directly to the form element, not to its container or children elements.
            </div>
        </div>
    `;

    // Fetch forms on component mount
    const fetchForms = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getForms();
            setForms(data);
            setError('');
        } catch (err) {
            setError('Failed to fetch forms');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchForms();
    }, [fetchForms]);

    // Handle form deletion
    const handleDelete = useCallback(async (id) => {
        if (window.confirm('Are you sure you want to delete this form?')) {
            try {
                await deleteForm(id);
                setForms(prevForms => prevForms.filter(form => form._id !== id));
            } catch (err) {
                setError('Failed to delete form');
                console.error(err);
            }
        }
    }, []);

    // Open modal for adding new form
    const handleAddForm = useCallback(() => {
        setIsEditing(false);
        setCurrentForm({
            formId: '',
            sourceUrl: '',
            formName: '',
            description: '',
            alias: '',       // Added new field
            formAttributeId: '', // Added new field
            fields: []
        });
        setShowModal(true);
    }, []);

    // Open modal for editing existing form
    const handleEditForm = useCallback((form) => {
        setIsEditing(true);
        setCurrentForm({
            ...form,
            // Ensure fields have all required properties
            alias: form.alias || '',  // Ensure alias exists
            formAttributeId: form.formAttributeId || '', // Ensure formAttributeId exists
            fields: form.fields.map(field => ({
                ...field,
                validation: field.validation || {
                    minLength: '',
                    maxLength: '',
                    regex: ''
                }
            }))
        });
        setShowModal(true);
    }, []);

    // Handle input changes for form fields
    const handleFormChange = useCallback((e) => {
        const { name, value } = e.target;
        setCurrentForm(prevForm => ({
            ...prevForm,
            [name]: value
        }));
    }, []);

    // Handle field input changes
    const handleFieldChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;

        setFieldState(prevField => {
            if (name.startsWith('validation.')) {
                const validationField = name.split('.')[1];
                return {
                    ...prevField,
                    validation: {
                        ...prevField.validation,
                        [validationField]: value
                    }
                };
            } else {
                return {
                    ...prevField,
                    [name]: type === 'checkbox' ? checked : value
                };
            }
        });
    }, []);

    // Add field to current form
    const handleAddField = useCallback(() => {
        // Validate field
        if (!fieldState.fieldName) {
            alert('Field name is required');
            return;
        }

        // Add field to current form
        setCurrentForm(prevForm => ({
            ...prevForm,
            fields: [...prevForm.fields, { ...fieldState }]
        }));

        // Reset current field
        setFieldState({
            fieldName: '',
            fieldType: 'text',
            required: false,
            defaultValue: '',
            validation: {
                minLength: '',
                maxLength: '',
                regex: ''
            }
        });
    }, [fieldState]);

    // Remove field from current form
    const handleRemoveField = useCallback((index) => {
        setCurrentForm(prevForm => {
            const updatedFields = [...prevForm.fields];
            updatedFields.splice(index, 1);
            return {
                ...prevForm,
                fields: updatedFields
            };
        });
    }, []);

    // Handle form submission
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setError('');

        // Validate form
        if (!currentForm.sourceUrl) {
            setError('Source URL is required');
            return;
        }

        try {
            const formData = {
                ...currentForm,
                createdAt: isEditing ? currentForm.createdAt : Date.now(),
                updatedAt: Date.now()
            };

            if (isEditing) {
                const updatedForm = await updateForm(currentForm._id, formData);
                setForms(prevForms => prevForms.map(form => form._id === updatedForm._id ? updatedForm : form));
                // Only close modal on success
                setShowModal(false);
            } else {
                const newForm = await createForm(formData);
                setForms(prevForms => [...prevForms, newForm]);
                // Only close modal on success
                setShowModal(false);
            }
        } catch (err) {
            // Set more detailed error message
            setError(`Failed to ${isEditing ? 'update' : 'create'} form: ${err.message || 'Unknown error'}`);
            console.error('Form submission error:', err);
        }
    }, [currentForm, isEditing, setError, setForms, setShowModal, updateForm, createForm]);

    // Handle showing tooltip
    const showTooltip = useCallback((columnName) => {
        setTooltipVisible(columnName);
    }, []);

    // Handle hiding tooltip
    const hideTooltip = useCallback(() => {
        setTooltipVisible(null);
    }, []);

    // Handle copying formAttributeId
    const handleCopyField = useCallback((value, field) => {
        navigator.clipboard.writeText(value)
            .then(() => {
                setCopiedField(field);
                setTimeout(() => setCopiedField(null), 2000);
            })
            .catch(err => {
                console.error('Failed to copy: ', err);
            });
    }, []);

    // Content rendering based on loading/error states
    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center my-12">
                    <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            );
        }

        if (error) {
            return (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <>
                {forms.length === 0 ? (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="p-8 text-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No forms found</h3>
                            <p className="mt-1 text-sm text-gray-500">Get started by creating a new form.</p>
                            <div className="mt-6">
                                <button
                                    onClick={handleAddForm}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    Add Form
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto shadow rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-help relative"
                                        onMouseEnter={() => showTooltip('alias')}
                                        onMouseLeave={hideTooltip}
                                    >
                                        <div className="flex items-center">
                                            Alias
                                            <svg className="ml-1 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            {tooltipVisible === 'alias' && (
                                                <div className="absolute z-10 top-full mt-1 left-0 bg-gray-900 text-white text-xs rounded py-1 px-2 w-48">
                                                    {columnInfo.alias}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-help relative"
                                        onMouseEnter={() => showTooltip('formAttributeId')}
                                        onMouseLeave={hideTooltip}
                                    >
                                        <div className="flex items-center">
                                            Form Attribute ID
                                            <svg className="ml-1 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            {tooltipVisible === 'formAttributeId' && (
                                                <div className="absolute z-10 top-full mt-1 left-0 bg-gray-900 text-white text-xs rounded py-1 px-2 w-48">
                                                    {columnInfo.formAttributeId}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-help relative"
                                        onMouseEnter={() => showTooltip('formId')}
                                        onMouseLeave={hideTooltip}
                                    >
                                        <div className="flex items-center">
                                            Form ID
                                            <svg className="ml-1 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            {tooltipVisible === 'formId' && (
                                                <div className="absolute z-10 top-full mt-1 left-0 bg-gray-900 text-white text-xs rounded py-1 px-2 w-48">
                                                    {columnInfo.formId}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-help relative"
                                        onMouseEnter={() => showTooltip('formName')}
                                        onMouseLeave={hideTooltip}
                                    >
                                        <div className="flex items-center">
                                            Form Name
                                            <svg className="ml-1 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            {tooltipVisible === 'formName' && (
                                                <div className="absolute z-10 top-full mt-1 left-0 bg-gray-900 text-white text-xs rounded py-1 px-2 w-48">
                                                    {columnInfo.formName}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-help relative"
                                        onMouseEnter={() => showTooltip('sourceUrl')}
                                        onMouseLeave={hideTooltip}
                                    >
                                        <div className="flex items-center">
                                            Source URL
                                            <svg className="ml-1 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            {tooltipVisible === 'sourceUrl' && (
                                                <div className="absolute z-10 top-full mt-1 left-0 bg-gray-900 text-white text-xs rounded py-1 px-2 w-48">
                                                    {columnInfo.sourceUrl}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-help relative"
                                        onMouseEnter={() => showTooltip('fields')}
                                        onMouseLeave={hideTooltip}
                                    >
                                        <div className="flex items-center">
                                            Fields
                                            <svg className="ml-1 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            {tooltipVisible === 'fields' && (
                                                <div className="absolute z-10 top-full mt-1 left-0 bg-gray-900 text-white text-xs rounded py-1 px-2 w-48">
                                                    {columnInfo.fields}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-help relative"
                                        onMouseEnter={() => showTooltip('createdAt')}
                                        onMouseLeave={hideTooltip}
                                    >
                                        <div className="flex items-center">
                                            Created At
                                            <svg className="ml-1 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            {tooltipVisible === 'createdAt' && (
                                                <div className="absolute z-10 top-full mt-1 left-0 bg-gray-900 text-white text-xs rounded py-1 px-2 w-48">
                                                    {columnInfo.createdAt}
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {forms.map(form => (
                                    <tr key={form._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{form.alias || '-'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap relative">
                                            <div className="flex items-center">
                                                <span className="text-sm text-gray-500">{form.formAttributeId || '-'}</span>
                                                {form.formAttributeId && (
                                                    <>
                                                        <button
                                                            className="ml-2 text-gray-400 hover:text-gray-600"
                                                            onClick={() => handleCopyField(form.formAttributeId, `formAttributeId-${form._id}`)}
                                                            title="Copy to clipboard"
                                                        >
                                                            {copiedField === `formAttributeId-${form._id}` ? (
                                                                <svg className="h-4 w-4 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            ) : (
                                                                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                                                                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                        <button
                                                            className="ml-1 h-5 w-5 flex items-center justify-center text-xs font-bold rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setTooltipVisible(
                                                                    tooltipVisible === `usage-${form._id}` ? null : `usage-${form._id}`
                                                                );
                                                            }}
                                                            title="How to use this ID"
                                                        >
                                                            ?
                                                        </button>
                                                        {tooltipVisible === `usage-${form._id}` && (
                                                            // <div className="absolute z-20 top-0 left-0 transform translate-y-8 bg-white border border-gray-200 shadow-lg rounded-md p-4 w-72"
                                                            //     dangerouslySetInnerHTML={{ __html: formAttributeUsageInstructions }} />
                                                            <FormAttributeTooltip id={form._id} />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {form.formId}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {form.formName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <a href={form.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">
                                                {form.sourceUrl.length > 30
                                                    ? form.sourceUrl.substring(0, 30) + '...'
                                                    : form.sourceUrl
                                                }
                                            </a>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span className="px-2 py-1 text-xs bg-blue-100 rounded text-blue-800">
                                                {form.fields.length}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(form.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleEditForm(form)}
                                                className="text-blue-600 hover:text-blue-900 mr-4"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(form._id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    };

    return (
        <Layout>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Forms</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Manage form configurations and field settings
                    </p>
                </div>
                <button
                    onClick={handleAddForm}
                    className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add Form
                </button>
            </div>

            {renderContent()}

            {showModal && (
                <FormModal
                    isEditing={isEditing}
                    currentForm={currentForm}
                    handleFormChange={handleFormChange}
                    handleSubmit={handleSubmit}
                    handleFieldChange={handleFieldChange}
                    fieldState={fieldState}
                    fieldTypes={fieldTypes}
                    handleAddField={handleAddField}
                    handleRemoveField={handleRemoveField}
                    setShowModal={setShowModal}
                    error={error} 
                />
            )}
        </Layout>
    );
};

export default FormList;
